// Phase 14 — Approval Configs admin UI
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Play, Edit3, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import PageHeader from "@/components/PageHeader";

const ENTITY_TYPES = ["order", "leave", "payment", "claim", "sale_return", "expense"];
const OPS = ["always", "eq", "ne", "gt", "gte", "lt", "lte", "in", "not_in"];
const SOURCES = ["role_code", "role_in_zone", "reporting_manager", "user_id"];
const TIMEOUTS = ["escalate_up", "auto_approve", "skip"];
const ROLES = ["SUPER_ADMIN", "MANAGEMENT", "SALES_HEAD", "ZSM", "RSM", "ASM", "SE", "DISTRIBUTOR", "ACCOUNTS", "MIS"];

const BLANK = () => ({
  entity_type: "order", name: "", is_active: true, priority: 100,
  condition: { field: "net_amount", op: "gt", value: 10000 },
  steps: [{ step_no: 1, approver_source: "reporting_manager", value: "", timeout_hours: 24, on_timeout: "escalate_up" }],
  zone_ids: [],
});

function StepEditor({ step, onChange, onRemove, index }) {
  return (
    <div className="border rounded-md p-3 bg-neutral-50 space-y-2" data-testid={`config-step-${index}`}>
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Orders" }, { label: "Approval configs" }]}
        title="Approval configs"
        subtitle="Per-entity approval chain policies with amount / stage rules."
        favorite={{ key: "admin.approval-configs", type: "screen", label: "Approval configs", route: "/admin/approval-configs" }}
        testid="approvalconfigspage-page-header"
      />

      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-neutral-500">Step {step.step_no || index + 1}</span>
        <div className="flex-1" />
        <button onClick={onRemove} className="text-rose-500" title="Remove step"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-[11px]">Approver source</Label>
          <Select value={step.approver_source} onValueChange={(v) => onChange({ ...step, approver_source: v })}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px]">Value (role code or user_id)</Label>
          {(step.approver_source === "role_code" || step.approver_source === "role_in_zone") ? (
            <Select value={step.value || "__pick__"} onValueChange={(v) => onChange({ ...step, value: v === "__pick__" ? "" : v })}>
              <SelectTrigger className="h-8"><SelectValue placeholder="Pick role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__pick__">— pick —</SelectItem>
                {ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Input className="h-8" value={step.value || ""} onChange={(e) => onChange({ ...step, value: e.target.value })}
                   placeholder={step.approver_source === "user_id" ? "user_id" : "n/a"}
                   disabled={step.approver_source === "reporting_manager"} />
          )}
        </div>
        <div>
          <Label className="text-[11px]">Timeout (hours)</Label>
          <Input className="h-8" type="number" min="1" value={step.timeout_hours ?? ""} onChange={(e) => onChange({ ...step, timeout_hours: e.target.value ? Number(e.target.value) : null })} />
        </div>
        <div>
          <Label className="text-[11px]">On timeout</Label>
          <Select value={step.on_timeout} onValueChange={(v) => onChange({ ...step, on_timeout: v })}>
            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
            <SelectContent>{TIMEOUTS.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

function ConfigEditor({ open, initial, onClose, onSaved }) {
  const [cfg, setCfg] = useState(initial || BLANK());
  const [preview, setPreview] = useState(null);
  const [sample, setSample] = useState('{"net_amount":25000}');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCfg(initial || BLANK()); setPreview(null); }, [initial, open]);

  function updateStep(idx, patch) {
    setCfg(c => ({ ...c, steps: c.steps.map((s, i) => i === idx ? patch : s) }));
  }
  function addStep() {
    setCfg(c => ({ ...c, steps: [...c.steps, { step_no: c.steps.length + 1,
        approver_source: "role_code", value: "ASM", timeout_hours: 24, on_timeout: "escalate_up" }] }));
  }
  function removeStep(idx) {
    setCfg(c => ({ ...c, steps: c.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_no: i + 1 })) }));
  }

  async function save() {
    setBusy(true);
    try {
      if (cfg.id) {
        const body = { ...cfg }; delete body.id; delete body.created_at; delete body.updated_at; delete body.created_by; delete body.updated_by;
        await api.patch(`/approval-configs/${cfg.id}`, body);
        toast.success("Config updated");
      } else {
        await api.post("/approval-configs", cfg);
        toast.success("Config created");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  }

  async function runPreview() {
    if (!cfg.id) { toast.error("Save the config first to preview"); return; }
    try {
      const body = { entity_sample: JSON.parse(sample) };
      const { data } = await api.post(`/approval-configs/${cfg.id}/preview`, body);
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e) || "Invalid sample JSON"); }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="config-editor-dialog">
        <DialogHeader>
          <DialogTitle>{cfg.id ? "Edit approval config" : "New approval config"}</DialogTitle>
          <DialogDescription>Define when the chain applies and who approves.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Entity type</Label>
              <Select value={cfg.entity_type} onValueChange={(v) => setCfg(c => ({ ...c, entity_type: v }))}>
                <SelectTrigger data-testid="cfg-entity-type"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={cfg.name} onChange={(e) => setCfg(c => ({ ...c, name: e.target.value }))} data-testid="cfg-name" />
            </div>
            <div>
              <Label>Priority</Label>
              <Input type="number" value={cfg.priority} onChange={(e) => setCfg(c => ({ ...c, priority: Number(e.target.value) }))} />
            </div>
          </div>

          <div className="border rounded-md p-3 bg-neutral-50">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Condition</div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-[11px]">Field</Label>
                <Input value={cfg.condition?.field || ""} onChange={(e) => setCfg(c => ({ ...c, condition: { ...c.condition, field: e.target.value } }))} data-testid="cfg-cond-field" />
              </div>
              <div>
                <Label className="text-[11px]">Operator</Label>
                <Select value={cfg.condition?.op || "always"} onValueChange={(v) => setCfg(c => ({ ...c, condition: { ...c.condition, op: v } }))}>
                  <SelectTrigger data-testid="cfg-cond-op"><SelectValue /></SelectTrigger>
                  <SelectContent>{OPS.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px]">Value</Label>
                <Input value={cfg.condition?.value ?? ""}
                       onChange={(e) => setCfg(c => ({ ...c, condition: { ...c.condition, value: isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value) } }))}
                       data-testid="cfg-cond-value" />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs uppercase tracking-wider text-neutral-500">Steps</span>
              <Button size="sm" variant="outline" onClick={addStep} data-testid="cfg-add-step"><Plus className="w-3 h-3 mr-1" /> Add step</Button>
            </div>
            <div className="space-y-2">
              {cfg.steps.map((s, i) => (
                <StepEditor key={i} step={s} index={i} onChange={(ns) => updateStep(i, ns)} onRemove={() => removeStep(i)} />
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Label htmlFor="cfg-active">Active</Label>
            <Switch id="cfg-active" checked={!!cfg.is_active} onCheckedChange={(v) => setCfg(c => ({ ...c, is_active: v }))} />
          </div>

          {cfg.id && (
            <div className="border rounded-md p-3 bg-sky-50 border-sky-200 space-y-2">
              <div className="text-xs uppercase tracking-wider text-sky-700 flex items-center justify-between">
                Preview chain
                <Button size="sm" variant="outline" onClick={runPreview} data-testid="cfg-preview-btn"><Play className="w-3 h-3 mr-1" /> Run</Button>
              </div>
              <Textarea rows={2} value={sample} onChange={(e) => setSample(e.target.value)} className="text-xs font-mono" placeholder='{"net_amount":25000}' />
              {preview && (
                <div className="space-y-1 text-sm">
                  {preview.resolved_chain.map(s => (
                    <div key={s.step_no} className="bg-white border rounded p-2 flex justify-between">
                      <span>#{s.step_no} · {s.approver_source} → <b>{s.resolved_user_name || s.resolved_user_id || "(unresolved)"}</b></span>
                      {s.timeout_hours && <span className="text-xs text-neutral-500">timeout {s.timeout_hours}h · {s.on_timeout}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="cfg-save-btn"><Save className="w-4 h-4 mr-1" /> {busy ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ApprovalConfigsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterEnt, setFilterEnt] = useState("");

  async function reload() {
    setLoading(true);
    try {
      const params = filterEnt ? `?entity_type=${filterEnt}` : "";
      const { data } = await api.get(`/approval-configs${params}`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { reload(); }, [filterEnt]);

  async function remove(cfg) {
    if (!window.confirm(`Deactivate "${cfg.name}"?`)) return;
    try {
      await api.delete(`/approval-configs/${cfg.id}`);
      toast.success("Deactivated"); reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  return (
    <div className="p-6 space-y-4" data-testid="approval-configs-page">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-semibold">Approval Configs</h1>
        <Select value={filterEnt || "__all__"} onValueChange={(v) => setFilterEnt(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-40 h-8"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All types</SelectItem>
            {ENTITY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setEditing(BLANK())} data-testid="cfg-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New config
        </Button>
      </div>

      {loading ? <div className="text-neutral-500 text-sm">Loading…</div>
       : items.length === 0 ? <div className="text-neutral-500 border border-dashed rounded-lg p-10 text-center">No configs yet.</div>
       : (
        <div className="space-y-2">
          {items.map(c => (
            <div key={c.id} className="border rounded-lg p-3 bg-white flex items-center gap-3" data-testid={`cfg-row-${c.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-neutral-500">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-neutral-100 mr-2">{c.entity_type}</span>
                  cond: {c.condition?.field} {c.condition?.op} {String(c.condition?.value)}
                  {" · "}steps: {(c.steps || []).length}
                  {" · "}priority: {c.priority}
                </div>
              </div>
              <span className={`text-[11px] px-2 py-0.5 rounded border ${c.is_active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-neutral-100 border-neutral-200 text-neutral-500"}`}>
                {c.is_active ? "active" : "inactive"}
              </span>
              <Button size="sm" variant="outline" onClick={() => setEditing(c)} data-testid={`cfg-edit-${c.id}`}><Edit3 className="w-3 h-3" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(c)} data-testid={`cfg-del-${c.id}`}><Trash2 className="w-3 h-3 text-rose-500" /></Button>
            </div>
          ))}
        </div>
       )}

      <ConfigEditor open={!!editing} initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />
    </div>
  );
}
